import React from "react";
import { Check, Sparkles } from "lucide-react";
import { STAGES, stageIndex, stageOf } from "../lib/bookingStatus";

/**
 * BookingTimeline — premium horizontal pipeline showing the customer's
 * journey through the 7 canonical TREL stages. Past stages get a filled
 * emerald dot, the current stage gets a coral pulsing dot, future stages
 * stay quiet stone. Mobile collapses to a compact 2-line summary instead
 * of forcing horizontal scroll.
 */
export default function BookingTimeline({ booking }) {
  const stage = stageOf(booking);
  if (stage === "canceled") {
    return (
      <div
        data-testid="booking-timeline"
        className="rounded-2xl bg-stone-50 border border-stone-200 px-5 py-4 text-[13.5px] text-stone-600"
      >
        This request was canceled.
      </div>
    );
  }
  const currentIdx = stageIndex(stage);

  return (
    <section
      data-testid="booking-timeline"
      data-current-stage={stage}
      className="rounded-2xl bg-white border border-stone-200/80 shadow-[0_8px_24px_-18px_rgba(7,26,45,0.18)] px-5 sm:px-6 py-5"
    >
      <div className="flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#C9A45C] font-semibold">
        <Sparkles size={12} /> TREL Workflow
      </div>

      {/* Mobile summary */}
      <div className="sm:hidden mt-3" data-testid="timeline-mobile">
        <div className="text-[#071A2D] font-serif text-[18px] leading-snug">
          {STAGES[currentIdx]?.label || "Selected"}
        </div>
        <div className="text-[12px] text-[#071A2D]/55 mt-0.5">
          Step {currentIdx + 1} of {STAGES.length}
        </div>
      </div>

      {/* Desktop pipeline */}
      <ol className="hidden sm:grid mt-5 grid-cols-7 gap-2 items-start" data-testid="timeline-desktop">
        {STAGES.map((s, i) => {
          const isDone = i < currentIdx;
          const isCurrent = i === currentIdx;
          return (
            <li
              key={s.key}
              data-testid={`timeline-stage-${s.key}`}
              data-state={isDone ? "done" : isCurrent ? "current" : "upcoming"}
              className="relative flex flex-col items-center text-center"
            >
              {/* Connector line to the next stage (skip on the last item) */}
              {i < STAGES.length - 1 && (
                <span
                  aria-hidden
                  className={`absolute top-3.5 left-1/2 right-[-50%] h-px ${isDone ? "bg-[#064E3B]" : "bg-stone-200"}`}
                />
              )}
              <span
                className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold
                  ${isDone
                    ? "bg-[#064E3B] text-white"
                    : isCurrent
                      ? "bg-[#FF7A45] text-white shadow-[0_0_0_4px_rgba(255,122,69,0.18)]"
                      : "bg-white border border-stone-300 text-stone-400"}`}
              >
                {isDone ? <Check size={13} strokeWidth={3} /> : i + 1}
              </span>
              <span
                className={`mt-2 text-[11.5px] leading-tight font-medium ${isCurrent ? "text-[#071A2D]" : isDone ? "text-[#064E3B]" : "text-stone-500"}`}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
